import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, formatUsd, formatUptime, formatRelative, type Instance, ApiError } from '../lib/api';
import { useAuth, scopedCan } from '../lib/auth';
import {
  Button, Card, StatusPill, Alert, Spinner, SecretReveal, Modal, Field, Input, Select,
} from '../components/ui';

// Templates whose machines run their own app, opened from here signed in as you (app-sso.ts).
const APP_TEMPLATES: Record<string, string> = {
  channel: 'Live Channel', 'minutes-manager': 'Minutes Manager', switcher: 'Engage Live Switcher',
};

interface Detail {
  access?: string | null;
  instance: Instance;
  operations: {
    id: string; operation: string; status: string; detail: string | null;
    requested_at: string; completed_at: string | null; requested_by: string | null;
  }[];
  runtime: {
    started_at: string; stopped_at: string | null; seconds: number | null;
    usd_per_hour: number | null; estimated_usd: number | null;
  }[];
  /**
   * Derived from the machine's template, so a multiviewer shows multiviewer
   * links and a vMix machine shows vMix ones. `url` is null for ports a
   * browser cannot open, which are shown as an address instead.
   */
  connection: { name: string; url: string | null; address: string; primary: boolean }[] | null;
  /** Minutes the template offers for idle shutdown; null when it has no watchdog. */
  autoShutdownChoices: number[] | null;
}

interface ResizeOption {
  instanceType: string;
  label: string | null;
  vcpu: number | null;
  memoryGiB: number | null;
  gpu: string | null;
  usdPerHour: number | null;
}

function autoShutdownLabel(minutes: number): string {
  if (minutes === 0) return 'Never';
  return minutes % 60 === 0
    ? `After ${minutes / 60} hour${minutes === 60 ? '' : 's'} idle`
    : `After ${minutes} min idle`;
}

interface AccessGrant {
  id: string;
  cidr: string;
  userEmail: string;
  grantedAt: string;
  expiresAt: string;
}

interface AccessState {
  enabled: boolean;
  yourIp: string;
  mine: AccessGrant | null;
  all: AccessGrant[];
  defaultHours: number;
  maxHours: number;
}

/** "3h 20m" / "18m" / "expired" — grants are short enough that days never apply. */
function timeLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';

  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

export function MachineDetail() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const auth = useAuth();
  const { user } = auth;

  const [data, setData] = useState<Detail | null>(null);
  // Buttons follow this user's level on this machine (view | use | edit | full).
  const can = scopedCan(auth.can, data?.access);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState<{ username: string; password: string; publicPassword?: string | null } | null>(null);
  const [imaging, setImaging] = useState(false);
  const [imageName, setImageName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessState | null>(null);
  const [granting, setGranting] = useState(false);
  const [signingOn, setSigningOn] = useState(false);
  const [resizing, setResizing] = useState<{
    current: ResizeOption | null;
    options: ResizeOption[];
  } | null>(null);
  const [resizeTo, setResizeTo] = useState('');
  const [resizeBusy, setResizeBusy] = useState(false);
  const [savingShutdown, setSavingShutdown] = useState(false);

  const load = useCallback(
    async (attempt = 0) => {
      if (!instanceId) return;
      try {
        setData(await api.get<Detail>(`/instances/${instanceId}`));
        setError(null);
      } catch (err) {
        // A machine can be a second or two ahead of the cache immediately after
        // launch. Retrying briefly beats showing "not found" to someone who has
        // just watched the launch succeed.
        if (err instanceof ApiError && err.status === 404 && attempt < 5) {
          setTimeout(() => void load(attempt + 1), 1500);
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Could not load this instance.');
      } finally {
        if (attempt === 0) setLoading(false);
      }
    },
    [instanceId],
  );

  const loadAccess = useCallback(async () => {
    if (!instanceId) return;
    try {
      setAccess(await api.get<AccessState>(`/instances/${instanceId}/access`));
    } catch {
      // Non-fatal. The rest of the page is still useful without it, and an
      // older server that does not have this endpoint should not break the
      // machine detail view outright.
      setAccess(null);
    }
  }, [instanceId]);

  useEffect(() => {
    void load();
    void loadAccess();
    const timer = setInterval(() => {
      void load();
      void loadAccess();
    }, 15_000);
    return () => clearInterval(timer);
  }, [load, loadAccess]);

  async function openResize(): Promise<void> {
    setError(null);
    try {
      const r = await api.get<{ current: ResizeOption | null; options: ResizeOption[] }>(
        `/instances/${instanceId}/resize`,
      );
      setResizing(r);
      setResizeTo(r.options[0]?.instanceType ?? '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the available sizes.');
    }
  }

  async function saveAutoShutdown(minutes: number): Promise<void> {
    setSavingShutdown(true);
    setError(null);
    try {
      await api.patch(`/instances/${instanceId}`, { autoShutdownMinutes: minutes });
      await load();
      setNotice(
        minutes === 0
          ? 'Auto-shutdown off. This instance now bills until someone stops it.'
          : `Auto-shutdown set to ${autoShutdownLabel(minutes).toLowerCase()}. A running instance picks this up within five minutes.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change auto-shutdown.');
    } finally {
      setSavingShutdown(false);
    }
  }

  async function act(action: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ message?: string }>(`/instances/${instanceId}/${action}`);
      await load();
      if (r?.message) setNotice(r.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not ${action}.`);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (!data) return <Alert tone="critical">{error ?? 'Instance not found.'}</Alert>;

  const { instance, connection } = data;
  const isRunning = instance.state === 'running';
  const shutdownTag: string | undefined = instance.tags['cloudprod:auto-shutdown-minutes'];
  // The server keeps retrying a start that AWS had no capacity for; the
  // pending operation's detail says how far it has got.
  const pendingStart = data.operations.find((o) => o.operation === 'start' && o.status === 'pending');
  const waitingForCapacity = instance.state === 'stopped' && !!pendingStart;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/machines" className="text-xs text-ink-muted hover:underline">
            ← Instances
          </Link>
          <h1 className="mt-1 text-lg font-semibold">{instance.name ?? instance.instanceId}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
            <StatusPill state={instance.state} pending={instance.pendingOperation} />
            <span className="font-mono">{instance.instanceId}</span>
            <span>{instance.instanceType}</span>
            <span>{instance.availabilityZone ?? instance.region}</span>
          </div>
        </div>

        {instance.managed && (
          <div className="flex flex-wrap gap-2">
            {instance.state === 'stopped' && can('resource:start') && !waitingForCapacity && (
              <Button variant="primary" loading={busy} onClick={() => void act('start')}>
                Start
              </Button>
            )}
            {waitingForCapacity && can('resource:start') && (
              <Button
                onClick={async () => {
                  try {
                    await api.delete(`/instances/${instanceId}/start`);
                    await load();
                    setNotice('Stopped waiting for capacity.');
                  } catch (err) {
                    setError(err instanceof ApiError ? err.message : 'Could not cancel.');
                  }
                }}
              >
                Stop trying
              </Button>
            )}
            {instance.state === 'stopped' && can('resource:edit') && !waitingForCapacity && (
              <Button variant="ghost" onClick={() => void openResize()}>Resize</Button>
            )}
            {isRunning && can('resource:stop') && (
              <Button loading={busy} onClick={() => void act('stop')}>Stop</Button>
            )}
            {isRunning && can('resource:reboot') && (
              <Button variant="ghost" loading={busy} onClick={() => void act('reboot')}>
                Reboot
              </Button>
            )}
            {can('template:image') && (
              <Button variant="ghost" onClick={() => setImaging(true)}>Create AMI</Button>
            )}
          </div>
        )}
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {notice && !waitingForCapacity && <Alert tone="good">{notice}</Alert>}
      {waitingForCapacity && (
        <Alert tone="warning" title="Waiting for AWS capacity">
          {pendingStart?.detail ?? 'Retrying the start until AWS has room for this instance.'} It
          starts by itself the moment AWS has room. Nothing else to do here; if it runs out of
          time, resize it and start again.
        </Alert>
      )}

      {instance.adopted && (
        <Alert tone="info" title="Adopted - protected">
          This instance was built outside the hub and has been adopted. An administrator can start
          and stop it from <Link to="/adopted" className="underline">Adopted resources</Link>; the
          hub never changes or terminates it.
        </Alert>
      )}

      {!instance.managed && !instance.adopted && (
        <Alert tone="info" title="Not managed by this dashboard">
          This instance was created outside CloudProduction, so it is read-only here. That is
          deliberate — it protects pre-existing infrastructure from accidental changes.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* --- Connect --- */}
          {isRunning && connection && connection.length > 0 && (
            <Card title="Connect">
              <div className="space-y-3 p-4">
                {/* Network access comes before the links, because without it
                    the links below simply time out with no explanation. */}
                {access?.enabled && (
                  <div className="rounded border border-grid p-3">
                    {access.mine ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="h-2 w-2 shrink-0 rounded-full bg-good" />
                          <span className="text-xs font-medium">
                            Open from {access.mine.cidr.replace('/32', '')}
                          </span>
                          <span className="text-xs text-ink-muted">
                            closes in {timeLeft(access.mine.expiresAt)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="ghost"
                            disabled={granting}
                            onClick={async () => {
                              setGranting(true);
                              setError(null);
                              try {
                                await api.post(`/instances/${instanceId}/access`);
                                await loadAccess();
                                setNotice('Access extended.');
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Could not extend access.');
                              } finally {
                                setGranting(false);
                              }
                            }}
                          >
                            Extend
                          </Button>
                          <Button
                            variant="ghost"
                            disabled={granting}
                            onClick={async () => {
                              setGranting(true);
                              setError(null);
                              try {
                                await api.delete(`/instances/${instanceId}/access/${access.mine!.id}`);
                                await loadAccess();
                                setNotice('Access closed.');
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Could not close access.');
                              } finally {
                                setGranting(false);
                              }
                            }}
                          >
                            Close now
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs text-ink-muted">
                          DCV is firewalled. Open it to this device — your address is{' '}
                          <code className="font-mono">{access.yourIp}</code>.
                        </div>
                        {can('resource:connect') ? (
                          <Button
                            disabled={granting}
                            onClick={async () => {
                              setGranting(true);
                              setError(null);
                              try {
                                await api.post(`/instances/${instanceId}/access`);
                                await loadAccess();
                                setNotice(
                                  `Access open for ${access.defaultHours}h. This applies to every running ` +
                                    'production instance, and is recorded in the audit log.',
                                );
                              } catch (err) {
                                setError(err instanceof ApiError ? err.message : 'Could not open access.');
                              } finally {
                                setGranting(false);
                              }
                            }}
                          >
                            {granting ? 'Opening…' : 'Open access from this device'}
                          </Button>
                        ) : (
                          <div className="text-xs text-ink-muted">
                            You do not have permission to open access. Ask an administrator, or tunnel
                            in with <code className="font-mono">connect-machine.ps1 -Forward dcv</code>.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Whose access is open matters on event day: if two people
                        are on the same machine, that is worth seeing. */}
                    {access.all.filter((g) => g.id !== access.mine?.id).length > 0 && (
                      <div className="mt-2 border-t border-grid pt-2 text-xs text-ink-muted">
                        Also open:{' '}
                        {access.all
                          .filter((g) => g.id !== access.mine?.id)
                          .map((g) => `${g.userEmail} (${timeLeft(g.expiresAt)})`)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Desktop sign-on. Only offered once network access is open,
                    because a sign-on link to an unreachable machine just hangs
                    and looks like the feature is broken. */}
                {can('resource:sso') && access?.mine && (
                  <div className="rounded border border-grid p-3">
                    {user && !user.totpEnabled ? (
                      <div className="space-y-2">
                        <div className="text-xs text-ink-muted">
                          Signing in without the Windows password needs an authenticator app on your
                          account — it is the only thing standing between a stolen session and this
                          desktop.
                        </div>
                        <Link to="/account" className="text-sm text-series-1 hover:underline">
                          Set one up in Account settings →
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs text-ink-muted">
                          Open the desktop signed in as Administrator, no password needed.
                        </div>
                        <Button
                          disabled={signingOn}
                          onClick={async () => {
                            setSigningOn(true);
                            setError(null);
                            try {
                              const res = await api.post<{ url: string }>(
                                `/instances/${instanceId}/sso`,
                              );
                              // Opened immediately rather than rendered as a link:
                              // the token is good once and dies in well under a
                              // minute, so a link sitting on screen is a link that
                              // will not work by the time anyone clicks it.
                              window.open(res.url, '_blank', 'noopener,noreferrer');
                            } catch (err) {
                              setError(
                                err instanceof ApiError ? err.message : 'Could not sign in.',
                              );
                            } finally {
                              setSigningOn(false);
                            }
                          }}
                        >
                          {signingOn ? 'Signing in…' : 'Open desktop'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* The primary port gets the full URL on its own line; the
                    rest are compact. Which ports appear, and what they are
                    called, comes from the machine's template. */}
                {connection
                  .filter((c) => c.primary)
                  .map((c) => (
                    <div key={c.name}>
                      <div className="text-xs text-ink-muted">{c.name}</div>
                      {c.url ? (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-series-1 hover:underline"
                        >
                          {c.url}
                        </a>
                      ) : (
                        <code className="font-mono text-sm">{c.address}</code>
                      )}
                    </div>
                  ))}

                {connection.filter((c) => !c.primary).length > 0 && (
                  <div className="grid gap-3 text-xs sm:grid-cols-3">
                    {connection
                      .filter((c) => !c.primary)
                      .map((c) => (
                        <div key={c.name}>
                          <div className="text-ink-muted">{c.name}</div>
                          {c.url ? (
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-series-1 hover:underline"
                            >
                              :{c.url.split(':').pop()}
                            </a>
                          ) : (
                            <code className="font-mono">{c.address}</code>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* The Live Channel's own app, signed in as you - no second
                    password. The hub vouches for you; the machine signs you in
                    as your account there (see app-sso.ts). */}
                {APP_TEMPLATES[instance.templateId ?? ''] && instance.state === 'running' && (
                  <div className="border-t border-grid pt-3">
                    <Button
                      onClick={async () => {
                        // Opened first, then pointed at the sign-in, so a popup
                        // blocker sees a click rather than a script.
                        const win = window.open('', '_blank');
                        try {
                          const r = await api.post<{ url: string }>(`/instances/${instanceId}/app-sso`, {});
                          if (win) win.location.href = r.url; else window.location.href = r.url;
                        } catch (err) {
                          win?.close();
                          setError(err instanceof ApiError ? err.message : `Could not open the ${APP_TEMPLATES[instance.templateId ?? '']}.`);
                        }
                      }}
                    >
                      Open {APP_TEMPLATES[instance.templateId ?? '']} - signed in as you
                    </Button>
                  </div>
                )}

                {can('resource:credentials') && (
                  <div className="border-t border-grid pt-3">
                    {credentials ? (
                      <div className="space-y-2">
                        <div className="text-xs text-ink-muted">
                          {credentials.username === 'Administrator'
                            ? 'Administrator password'
                            : <>Sign in as <code className="font-mono">{credentials.username}</code> (superadmin)</>}
                          {' '}— this access is recorded in the audit log.
                        </div>
                        <SecretReveal value={credentials.password} label="password" />
                        {credentials.publicPassword && (
                          <>
                            <div className="pt-2 text-xs text-ink-muted">
                              Public view password — for{' '}
                              <code className="font-mono">http://{instance.publicIp ?? '…'}/</code>,
                              the read-only view anyone with it can open.
                            </div>
                            <SecretReveal value={credentials.publicPassword} label="public view password" />
                          </>
                        )}
                      </div>
                    ) : (
                      <Button
                        onClick={async () => {
                          try {
                            setCredentials(
                              await api.get<{ username: string; password: string; publicPassword?: string | null }>(
                                `/instances/${instanceId}/credentials`,
                              ),
                            );
                          } catch (err) {
                            setError(err instanceof ApiError ? err.message : 'No stored credentials.');
                          }
                        }}
                      >
                        {APP_TEMPLATES[instance.templateId ?? ''] ? 'Reveal sign-in password' : 'Reveal Administrator password'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* --- History --- */}
          <Card title="Recent actions">
            {data.operations.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-ink-muted">
                No recorded actions.
              </div>
            ) : (
              <div className="divide-y divide-grid">
                {data.operations.map((op) => (
                  <div key={op.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-xs">
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{
                        background:
                          op.status === 'success' ? 'var(--color-good)'
                          : op.status === 'failed' ? 'var(--color-critical)'
                          : 'var(--color-warning)',
                      }}
                    />
                    <span className="w-20 font-medium capitalize">{op.operation}</span>
                    <span className="flex-1 text-ink-muted">
                      {op.detail ?? op.status}
                    </span>
                    <span className="hidden text-ink-muted sm:inline">{op.requested_by ?? 'system'}</span>
                    <span className="w-20 text-right text-ink-muted">
                      {formatRelative(op.requested_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* --- Sidebar --- */}
        <div className="space-y-4">
          <Card title="Cost">
            <div className="space-y-2 p-4 text-sm">
              <Row label="Rate" value={instance.usdPerHour ? `${formatUsd(instance.usdPerHour, 3)}/hr` : '—'} />
              <Row
                label="This session"
                value={isRunning ? formatUsd(instance.currentSessionUsd) : '—'}
              />
              <Row label="Uptime" value={isRunning ? formatUptime(instance.launchTime) : '—'} />
              <Row
                label="Per 8-hr day"
                value={instance.usdPerHour ? formatUsd(instance.usdPerHour * 8) : '—'}
              />
            </div>
          </Card>

          <Card title="Details">
            <div className="space-y-2 p-4 text-sm">
              <Row label="Template" value={instance.templateId ?? '—'} />
              <Row label="Owner" value={instance.ownerEmail ?? '—'} />
              <Row label="Public IP" value={instance.publicIp ?? '—'} mono />
              <StaticIpRow
                tag={instance.tags['cloudprod:static-ip'] ?? null}
                running={instance.state === 'running'}
                canEdit={instance.managed && can('resource:edit')}
                instanceId={instance.instanceId}
                onChanged={() => void load()}
                onError={setError}
              />
              <Row label="Private IP" value={instance.privateIp ?? '—'} mono />
              {data.autoShutdownChoices && instance.managed && can('resource:edit') ? (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-ink-muted">Auto-shutdown</span>
                  <Select
                    className="!w-auto"
                    disabled={savingShutdown}
                    value={shutdownTag ?? ''}
                    onChange={(e) => void saveAutoShutdown(Number(e.target.value))}
                  >
                    {/* A machine launched with a value the template no longer
                        offers still shows what it actually has. */}
                    {!data.autoShutdownChoices.includes(Number(shutdownTag ?? NaN)) && (
                      <option value={shutdownTag ?? ''}>
                        {shutdownTag ? autoShutdownLabel(Number(shutdownTag)) : 'Not set'}
                      </option>
                    )}
                    {data.autoShutdownChoices.map((m) => (
                      <option key={m} value={String(m)}>{autoShutdownLabel(m)}</option>
                    ))}
                  </Select>
                </div>
              ) : (
                <Row
                  label="Auto-shutdown"
                  value={shutdownTag === undefined ? '—' : autoShutdownLabel(Number(shutdownTag))}
                />
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={resizing !== null}
        onClose={() => setResizing(null)}
        title="Resize"
        footer={
          <>
            <Button onClick={() => setResizing(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!resizeTo}
              loading={resizeBusy}
              onClick={async () => {
                setResizeBusy(true);
                try {
                  await api.post(`/instances/${instanceId}/resize`, { instanceType: resizeTo });
                  await load();
                  setNotice(`Now ${resizeTo}. Start it when you are ready.`);
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not resize.');
                } finally {
                  setResizing(null);
                  setResizeBusy(false);
                }
              }}
            >
              Change size
            </Button>
          </>
        }
      >
        {resizing && (
          <div className="space-y-3 text-sm">
            <p className="text-ink-secondary">
              Everything on the disk stays as it is. Only sizes this instance can boot on, in its
              own zone ({instance.availabilityZone}), are listed.
            </p>
            {resizing.options.length === 0 ? (
              <Alert tone="info">No other sizes are available for this instance in its zone.</Alert>
            ) : (
              <div className="divide-y divide-grid rounded border border-grid">
                {[resizing.current, ...resizing.options]
                  .filter((o): o is ResizeOption => o !== null)
                  .map((o) => {
                    const isCurrent = o.instanceType === resizing.current?.instanceType;
                    return (
                      <label
                        key={o.instanceType}
                        className={`flex items-center gap-3 px-3 py-2 ${isCurrent ? 'opacity-60' : 'cursor-pointer'}`}
                      >
                        <input
                          type="radio"
                          name="resize"
                          disabled={isCurrent}
                          checked={resizeTo === o.instanceType}
                          onChange={() => setResizeTo(o.instanceType)}
                        />
                        <div className="flex-1">
                          <div className="font-mono text-xs">
                            {o.instanceType}
                            {isCurrent && <span className="ml-2 font-sans text-ink-muted">current</span>}
                          </div>
                          <div className="text-xs text-ink-muted">
                            {[
                              o.vcpu && `${o.vcpu} vCPU`,
                              o.memoryGiB && `${o.memoryGiB} GiB`,
                              o.gpu,
                              o.label,
                            ].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div className="text-right text-xs tabular">
                          {o.usdPerHour ? `${formatUsd(o.usdPerHour, 3)}/hr` : '—'}
                        </div>
                      </label>
                    );
                  })}
              </div>
            )}
            <p className="text-xs text-ink-muted">
              Also the way round &ldquo;no capacity&rdquo; on start: AWS runs out per size per
              zone, so another size is often available when this one is not.
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={imaging}
        onClose={() => setImaging(false)}
        title="Create AMI"
        footer={
          <>
            <Button onClick={() => setImaging(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!imageName.trim()}
              onClick={async () => {
                try {
                  const r = await api.post<{ message: string }>(
                    `/instances/${instanceId}/image`,
                    { name: imageName.trim() },
                  );
                  setNotice(r.message);
                  setImaging(false);
                  setImageName('');
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not create the image.');
                }
              }}
            >
              Create image
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert tone="warning" title="The instance will reboot">
            Capturing reboots the instance so the filesystem is consistent. Do not do this during a
            production. It takes 10–20 minutes to become available.
          </Alert>
          <Field label="Image name" hint="Future launches can use this instead of a base image.">
            <Input
              value={imageName}
              onChange={(e) => setImageName(e.target.value)}
              placeholder="vmix-golden-2026-08"
              autoFocus
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Whether the machine keeps its address through a stop and start, and the
 * control to change that. The tag reads "pending" between launch and the
 * address being attached, and "none" if attaching failed.
 */
function StaticIpRow({ tag, running, canEdit, instanceId, onChanged, onError }: {
  tag: string | null; running: boolean; canEdit: boolean; instanceId: string;
  onChanged: () => void; onError: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const isStatic = !!tag && tag !== 'pending' && tag !== 'none';
  async function change(add: boolean): Promise<void> {
    const warn = add
      ? (running ? 'The instance’s address changes now, to one it keeps for good. Anything pointed at the current address - an encoder, a bookmark - needs the new one. Streaming restarts for a moment to use it. Go ahead?' : 'Give this instance an address it keeps through every stop and start?')
      : 'Give the static address back? The instance gets a changing address again, and anything using this one stops reaching it.';
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      if (add) await api.post(`/instances/${instanceId}/static-ip`, {});
      else await api.delete(`/instances/${instanceId}/static-ip`);
      setTimeout(onChanged, 1500);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not change the address.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-muted">Address</span>
      <span className="flex items-center gap-2 text-right">
        <span className="text-xs">
          {isStatic ? 'Static - kept through stop/start' : tag === 'pending' ? 'Static - attaching…' : 'Changes on every start'}
        </span>
        {canEdit && tag !== 'pending' && (
          <Button onClick={() => void change(!isStatic)} disabled={busy}>
            {busy ? '…' : isStatic ? 'Release' : 'Make static'}
          </Button>
        )}
      </span>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className={`min-w-0 break-all text-right ${mono ? 'font-mono text-xs' : ''} tabular`}>{value}</span>
    </div>
  );
}
